// src/pages/Security/PermissionManager.jsx
import React, { useState, useEffect, useMemo, useCallback, useDeferredValue, useRef } from 'react';
import axiosInstance from '../api/axiosInstance';
import { useAuth } from '../context/AuthContext';
import {
  Table,
  Card,
  Button,
  Space,
  Typography,
  Tag,
  Modal,
  notification,
  Alert,
  Input,
  Form,
  Checkbox,
  Badge,
  Descriptions,
  Tabs,
  Row,
  Col,
  Spin,
} from 'antd';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSyncAlt,
  FaInfoCircle,
  FaKey,
  FaUsers,
  FaUserShield,
} from 'react-icons/fa';

const { Text, Title } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;
const { confirm } = Modal;

// Custom hook for debounce
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
};

// Tab content components for better code organization
const PermissionsTab = React.memo(({ 
  permissions, 
  permissionsLoading, 
  error,
  permissionColumns,
  form,
  setEditingId,
  setModalVisible,
  handleDeletePermission,
  setError // Added setError prop
}) => {
  return (
    <div>
      {error && (
        <Alert 
          type="error" 
          closable 
          showIcon 
          message="Error" 
          description={error} 
          style={{ marginBottom: 16 }} 
          onClose={() => setError('')}
        />
      )}
      <Table
        rowKey="permissionId"
        columns={permissionColumns}
        dataSource={permissions}
        loading={permissionsLoading}
        pagination={{ 
          pageSize: 10, 
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => 
            `${range[0]}-${range[1]} of ${total} permissions`,
        }}
        scroll={{ x: 800 }}
        size="middle"
        locale={{ emptyText: 'No permissions found' }}
      />
    </div>
  );
});

const RolePermissionsTab = React.memo(({
  roles,
  rolesLoading,
  permissions,
  assignLoading,
  selectedRole,
  rolePermissions,
  roleSearch,
  setRoleSearch,
  debouncedRoleSearch,
  handleRoleSelect,
  togglePermission,
  bulkAssignPermissions,
  RoleInfo,
  BulkButtons
}) => {
  const filteredRoles = useMemo(() => {
    const q = debouncedRoleSearch.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter(
      (r) => (r.name ?? '').toLowerCase().includes(q) || (r.id ?? '').toLowerCase().includes(q),
    );
  }, [roles, debouncedRoleSearch]);

  const rolePermSet = useMemo(() => new Set(rolePermissions), [rolePermissions]);

  const assignmentColumns = useMemo(
    () => [
      {
        title: 'Permission',
        dataIndex: 'name',
        render: (t, r) => (
          <Space>
            <FaKey style={{ color: rolePermSet.has(r.permissionId) ? '#52c41a' : '#ff4d4f' }} />
            <Text>{t}</Text>
          </Space>
        ),
      },
      {
        title: 'Status',
        width: 140,
        render: (_, r) => (
          <Tag color={rolePermSet.has(r.permissionId) ? 'green' : 'red'}>
            {rolePermSet.has(r.permissionId) ? 'Assigned' : 'Unassigned'}
          </Tag>
        ),
      },
      {
        title: 'Action',
        width: 120,
        render: (_, r) => (
          <Checkbox
            checked={rolePermSet.has(r.permissionId)}
            onChange={(e) => togglePermission(r.permissionId, e.target.checked)}
            disabled={!selectedRole || assignLoading}
          />
        ),
      },
    ],
    [assignLoading, rolePermSet, selectedRole, togglePermission],
  );

  const rolesPickerColumns = useMemo(
    () => [
      { 
        title: 'Role', 
        dataIndex: 'name', 
        key: 'name', 
        width: 200,
        render: (text) => <Text strong>{text}</Text>
      },
      { 
        title: 'ID', 
        dataIndex: 'id', 
        key: 'id', 
        ellipsis: true,
        render: (text) => <Text type="secondary">{text}</Text>
      },
    ],
    [],
  );

  return (
    <Row gutter={[16, 16]}>
      {/* LEFT: roles table picker */}
      <Col xs={24} md={8}>
        <Card 
          size="small" 
          title="Select Role" 
          extra={<Badge count={filteredRoles.length} showZero color="blue" />}
        >
          <Input.Search
            placeholder="Search roles by name or ID"
            value={roleSearch}
            onChange={(e) => setRoleSearch(e.target.value)}
            allowClear
            style={{ marginBottom: 12 }}
          />
          <Table
            size="small"
            rowKey="id"
            columns={rolesPickerColumns}
            dataSource={filteredRoles}
            loading={rolesLoading}
            pagination={{ 
              pageSize: 8, 
              showSizeChanger: false,
              simple: true,
            }}
            scroll={{ y: 360 }}
            rowSelection={{
              type: 'radio',
              selectedRowKeys: selectedRole ? [selectedRole] : [],
              onChange: (keys) => handleRoleSelect(keys[0]),
            }}
            onRow={(record) => ({
              onClick: () => handleRoleSelect(record.id),
              style: { cursor: 'pointer' }
            })}
            locale={{ emptyText: 'No roles found' }}
          />
        </Card>
      </Col>

      {/* RIGHT: selected role details + permissions assignment */}
      <Col xs={24} md={16}>
        <Space direction="vertical" style={{ width: '100%' }}>
          {RoleInfo}
          <BulkButtons type="role" />
          <Table
            rowKey="permissionId"
            columns={assignmentColumns}
            dataSource={permissions}
            loading={assignLoading}
            size="middle"
            pagination={{ 
              pageSize: 10, 
              showSizeChanger: true,
              showTotal: (total) => `${total} permissions`,
            }}
            scroll={{ x: 600, y: 520 }}
            locale={{ emptyText: 'No permissions available' }}
          />
        </Space>
      </Col>
    </Row>
  );
});

const UserRolesTab = React.memo(({
  users,
  usersLoading,
  roles,
  assignLoading,
  selectedUser,
  userRoles,
  userSearch,
  setUserSearch,
  debouncedUserSearch,
  handleUserSelect,
  toggleUserRole,
  bulkAssignRoles,
  UserInfo,
  BulkButtons
}) => {
  const filteredUsers = useMemo(() => {
    const q = debouncedUserSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        `${u.firstName ?? ''} ${u.lastName ?? ''}`.toLowerCase().includes(q) ||
        (u.email ?? '').toLowerCase().includes(q),
    );
  }, [users, debouncedUserSearch]);

  const userRoleSet = useMemo(() => new Set(userRoles), [userRoles]);

  const userRoleColumns = useMemo(
    () => [
      {
        title: 'Role',
        dataIndex: 'name',
        render: (t, r) => (
          <Space>
            <FaUserShield style={{ color: userRoleSet.has(r.id) ? '#52c41a' : '#ff4d4f' }} />
            <Text>{t}</Text>
          </Space>
        ),
      },
      {
        title: 'Status',
        width: 140,
        render: (_, r) => (
          <Tag color={userRoleSet.has(r.id) ? 'green' : 'red'}>
            {userRoleSet.has(r.id) ? 'Assigned' : 'Unassigned'}
          </Tag>
        ),
      },
      {
        title: 'Action',
        width: 120,
        render: (_, r) => (
          <Checkbox
            checked={userRoleSet.has(r.id)}
            onChange={(e) => toggleUserRole(r.id, e.target.checked)}
            disabled={!selectedUser || assignLoading}
          />
        ),
      },
    ],
    [assignLoading, selectedUser, toggleUserRole, userRoleSet],
  );

  const usersPickerColumns = useMemo(
    () => [
      {
        title: 'User',
        key: 'name',
        width: 220,
        render: (_, u) => (
          <Text strong>
            {`${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || <Text type="secondary">—</Text>}
          </Text>
        ),
      },
      { 
        title: 'Email', 
        dataIndex: 'email', 
        key: 'email', 
        ellipsis: true,
        render: (text) => text || <Text type="secondary">—</Text>
      },
    ],
    [],
  );

  return (
    <Row gutter={[16, 16]}>
      {/* LEFT: users table picker */}
      <Col xs={24} md={8}>
        <Card 
          size="small" 
          title="Select User" 
          extra={<Badge count={filteredUsers.length} showZero color="blue" />}
        >
          <Input.Search
            placeholder="Search users by name or email"
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            allowClear
            style={{ marginBottom: 12 }}
          />
          <Table
            size="small"
            rowKey="id"
            columns={usersPickerColumns}
            dataSource={filteredUsers}
            loading={usersLoading}
            pagination={{ 
              pageSize: 8, 
              showSizeChanger: false,
              simple: true,
            }}
            scroll={{ y: 360 }}
            rowSelection={{
              type: 'radio',
              selectedRowKeys: selectedUser ? [selectedUser] : [],
              onChange: (keys) => handleUserSelect(keys[0]),
            }}
            onRow={(record) => ({
              onClick: () => handleUserSelect(record.id),
              style: { cursor: 'pointer' }
            })}
            locale={{ emptyText: 'No users found' }}
          />
        </Card>
      </Col>

      {/* RIGHT: selected user details + role assignment */}
      <Col xs={24} md={16}>
        <Space direction="vertical" style={{ width: '100%' }}>
          {UserInfo}
          <BulkButtons type="user" />
          <Table
            rowKey="id"
            columns={userRoleColumns}
            dataSource={roles}
            loading={assignLoading}
            size="middle"
            pagination={{ 
              pageSize: 10, 
              showSizeChanger: true,
              showTotal: (total) => `${total} roles`,
            }}
            scroll={{ x: 600, y: 520 }}
            locale={{ emptyText: 'No roles available' }}
          />
        </Space>
      </Col>
    </Row>
  );
});

const PermissionManager = () => {
  const { authData } = useAuth();

  // ─── state ─────────────────────────────────────────────
  const [activeKey, setActiveKey] = useState('1');

  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);

  const [categories, setCategories] = useState([]);

  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState([]);

  const [selectedUser, setSelectedUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  // Separate loading states for each tab
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  
  const [error, setError] = useState('');

  // Track which tabs have been loaded
  const [loadedTabs, setLoadedTabs] = useState(new Set(['1'])); // Tab 1 loads initially

  // local search state for pickers with debounce
  const [roleSearch, setRoleSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');

  const debouncedRoleSearch = useDebounce(roleSearch, 300);
  const debouncedUserSearch = useDebounce(userSearch, 300);

  const [form] = Form.useForm();
  const mountedRef = useRef(true);

  // ─── lifecycle: mount/unmount guard ─────────────────────
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ─── enhanced data loaders with timeouts ───────────────
  const fetchWithTimeout = async (url, options = {}, timeout = 10000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    try {
      const response = await axiosInstance.get(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  };

  const fetchPermissions = useCallback(async (signal) => {
    if (!authData?.hotelId) return;
    
    setPermissionsLoading(true);
    setError('');
    try {
      const res = await fetchWithTimeout(`/RolePermission/Permissions`, {
        params: { hotelId: authData.hotelId },
        signal,
      });
      
      if (!mountedRef.current) return;
      setPermissions(res.data);
      setCategories([...new Set((res.data || []).map((p) => p.category ?? 'General'))]);
    } catch (err) {
      if (!mountedRef.current || err.name === 'CanceledError' || err.name === 'AbortError') return;
      
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load permissions';
      setError(errorMsg);
      notification.error({ 
        message: 'Load Error', 
        description: errorMsg,
        duration: 4,
      });
    } finally {
      if (mountedRef.current) setPermissionsLoading(false);
    }
  }, [authData?.hotelId]);

  const fetchRoles = useCallback(async (signal) => {
    if (!authData?.hotelId) return;
    
    setRolesLoading(true);
    setError('');
    try {
      const res = await fetchWithTimeout(`/Roles/ByHotel/${authData.hotelId}`, { signal });
      if (!mountedRef.current) return;
      setRoles(res.data);
    } catch (err) {
      if (!mountedRef.current || err.name === 'CanceledError' || err.name === 'AbortError') return;
      
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load roles';
      setError(errorMsg);
      notification.error({ 
        message: 'Load Error', 
        description: errorMsg,
        duration: 4,
      });
    } finally {
      if (mountedRef.current) setRolesLoading(false);
    }
  }, [authData?.hotelId]);

  const fetchUsers = useCallback(async (signal) => {
    if (!authData?.hotelId) return;
    
    setUsersLoading(true);
    setError('');
    try {
      const res = await fetchWithTimeout(`/User`, {
        params: { hotelId: authData.hotelId },
        signal,
      });
      if (!mountedRef.current) return;
      setUsers(res.data);
    } catch (err) {
      if (!mountedRef.current || err.name === 'CanceledError' || err.name === 'AbortError') return;
      
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load users';
      setError(errorMsg);
      notification.error({ 
        message: 'Load Error', 
        description: errorMsg,
        duration: 4,
      });
    } finally {
      if (mountedRef.current) setUsersLoading(false);
    }
  }, [authData?.hotelId]);

  // ─── initial: only permissions (faster first paint) ────
  useEffect(() => {
    if (!authData?.hotelId) return;
    
    const controller = new AbortController();
    const loadTimeout = setTimeout(() => {
      if (permissionsLoading) {
        controller.abort();
        setPermissionsLoading(false);
        setError('Permissions load timeout. Please try again.');
      }
    }, 15000);

    fetchPermissions(controller.signal);

    return () => {
      controller.abort();
      clearTimeout(loadTimeout);
    };
  }, [authData?.hotelId, fetchPermissions]);

  // ─── lazy load by tab ───────────────────────────────────
  const handleTabChange = (key) => {
    setActiveKey(key);
    
    // If tab hasn't been loaded yet, load its data
    if (!loadedTabs.has(key)) {
      const controller = new AbortController();
      
      switch (key) {
        case '2': // Role-Permissions tab
          fetchRoles(controller.signal);
          break;
        case '3': // User-Roles tab
          fetchUsers(controller.signal);
          break;
        default:
          break;
      }
      
      // Mark tab as loaded
      setLoadedTabs(prev => new Set(prev).add(key));
      
      // Cleanup on tab change if needed
      return () => controller.abort();
    }
  };

  // ─── Permission CRUD ───────────────────────────────────
  const handleCreatePermission = useCallback(async () => {
    try {
      setModalLoading(true);
      const values = await form.validateFields();
      const payload = { ...values, hotelId: authData?.hotelId };
      const { data } = await axiosInstance.post('/RolePermission/Permissions', payload);
      
      setPermissions((prev) => [...prev, data]);
      if (!categories.includes(payload.category)) {
        setCategories((prev) => [...prev, payload.category]);
      }
      
      form.resetFields();
      setModalVisible(false);
      notification.success({ 
        message: 'Success', 
        description: 'Permission created successfully',
        duration: 3,
      });
    } catch (err) {
      if (err?.errorFields) return; // form validation error
      
      const errorMsg = err.response?.data?.message || 'Failed to create permission';
      setError(errorMsg);
      notification.error({ 
        message: 'Create Error', 
        description: errorMsg,
      });
    } finally {
      setModalLoading(false);
    }
  }, [authData?.hotelId, categories, form]);

  const handleUpdatePermission = useCallback(async () => {
    try {
      setModalLoading(true);
      const values = await form.validateFields();
      const { data } = await axiosInstance.put(`/RolePermission/Permissions/${editingId}`, values);
      
      setPermissions((prev) => prev.map((p) => (p.permissionId === editingId ? data : p)));
      setEditingId(null);
      setModalVisible(false);
      form.resetFields();
      
      notification.success({ 
        message: 'Success', 
        description: 'Permission updated successfully',
        duration: 3,
      });
    } catch (err) {
      if (err?.errorFields) return;
      
      const errorMsg = err.response?.data?.message || 'Failed to update permission';
      setError(errorMsg);
      notification.error({ 
        message: 'Update Error', 
        description: errorMsg,
      });
    } finally {
      setModalLoading(false);
    }
  }, [editingId, form]);

  const handleDeletePermission = useCallback((permissionId) => {
    confirm({
      title: 'Delete Permission',
      content: 'Are you sure you want to delete this permission? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      maskClosable: true,
      onOk: async () => {
        try {
          await axiosInstance.delete(`/RolePermission/Permissions/${permissionId}`);
          setPermissions((prev) => prev.filter((p) => p.permissionId !== permissionId));
          setRolePermissions((prev) => prev.filter((id) => id !== permissionId));
          
          notification.success({ 
            message: 'Success', 
            description: 'Permission deleted successfully',
            duration: 3,
          });
        } catch (err) {
          const errorMsg = err.response?.data?.message || 'Failed to delete permission';
          setError(errorMsg);
          notification.error({ 
            message: 'Delete Error', 
            description: errorMsg,
          });
        }
      },
    });
  }, []);

  // ─── Role-permission assignment ─────────────────────────
  const handleRoleSelect = useCallback(async (roleId) => {
    try {
      setAssignLoading(true);
      if (!roleId) {
        setSelectedRole(null);
        setRolePermissions([]);
        return;
      }
      
      const { data } = await fetchWithTimeout(`/RolePermission/Roles/${roleId}/Permissions`);
      setSelectedRole(roleId);
      setRolePermissions((data || []).map((p) => p.permissionId));
    } catch (err) {
      notification.error({ 
        message: 'Load Error', 
        description: 'Failed to load role permissions',
      });
    } finally {
      setAssignLoading(false);
    }
  }, []);

  const togglePermission = useCallback(
    async (permissionId, checked) => {
      if (!selectedRole) {
        notification.warning({ 
          message: 'Warning', 
          description: 'Please select a role first',
        });
        return;
      }
      
      try {
        setAssignLoading(true);
        if (checked) {
          await axiosInstance.post(`/RolePermission/Roles/${selectedRole}/Permissions`, {
            permissionIds: [permissionId],
          });
          setRolePermissions((prev) => (prev.includes(permissionId) ? prev : [...prev, permissionId]));
        } else {
          await axiosInstance.delete(`/RolePermission/Roles/${selectedRole}/Permissions/${permissionId}`);
          setRolePermissions((prev) => prev.filter((id) => id !== permissionId));
        }
      } catch (err) {
        notification.error({
          message: 'Update Error',
          description: 'Failed to update role permissions',
        });
      } finally {
        setAssignLoading(false);
      }
    },
    [selectedRole],
  );

  const bulkAssignPermissions = useCallback(
    async (ids) => {
      if (!selectedRole) return;
      
      try {
        setAssignLoading(true);
        await axiosInstance.post(`/RolePermission/Roles/${selectedRole}/Permissions`, { 
          permissionIds: ids 
        });
        setRolePermissions(ids);
        
        notification.success({
          message: 'Success',
          description: 'All permissions assigned successfully',
        });
      } catch (err) {
        notification.error({
          message: 'Bulk Assign Error',
          description: 'Failed to assign permissions',
        });
      } finally {
        setAssignLoading(false);
      }
    },
    [selectedRole],
  );

  // ─── User-role assignment ──────────────────────────────
  const handleUserSelect = useCallback(async (userId) => {
    try {
      setAssignLoading(true);
      if (!userId) {
        setSelectedUser(null);
        setUserRoles([]);
        return;
      }
      
      const { data } = await fetchWithTimeout(`/User/${userId}/Roles`);
      setSelectedUser(userId);
      setUserRoles((data?.roles || []).map((r) => r.id));
    } catch (err) {
      notification.error({
        message: 'Load Error',
        description: err.response?.data?.message || err.message || 'Failed to load user roles',
      });
    } finally {
      setAssignLoading(false);
    }
  }, []);

  const toggleUserRole = useCallback(
    async (roleId, checked) => {
      if (!selectedUser) {
        notification.warning({ 
          message: 'Warning', 
          description: 'Please select a user first' 
        });
        return;
      }
      
      try {
        setAssignLoading(true);
        if (checked) {
          await axiosInstance.post(`/User/${selectedUser}/Roles`, { roleIds: [roleId] });
          setUserRoles((prev) => (prev.includes(roleId) ? prev : [...prev, roleId]));
        } else {
          await axiosInstance.delete(`/User/${selectedUser}/Roles/${roleId}`);
          setUserRoles((prev) => prev.filter((id) => id !== roleId));
        }
      } catch (err) {
        notification.error({
          message: 'Update Error',
          description: err.response?.data?.message || err.message || 'Failed to update user role',
        });
      } finally {
        setAssignLoading(false);
      }
    },
    [selectedUser],
  );

  const bulkAssignRoles = useCallback(
    async (ids) => {
      if (!selectedUser) return;
      
      try {
        setAssignLoading(true);
        await axiosInstance.post(`/User/${selectedUser}/Roles`, { roleIds: ids });
        setUserRoles(ids);
        
        notification.success({
          message: 'Success',
          description: 'All roles assigned successfully',
        });
      } catch (err) {
        notification.error({
          message: 'Bulk Assign Error',
          description: err.response?.data?.message || err.message || 'Failed to bulk assign roles',
        });
      } finally {
        setAssignLoading(false);
      }
    },
    [selectedUser],
  );

  // ─── column definitions (memoized) ─────────────────────
  const permissionColumns = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        render: (text) => (
          <Space>
            <FaKey style={{ color: '#1890ff' }} />
            <Text strong>{text}</Text>
          </Space>
        ),
      },
      { 
        title: 'Description', 
        dataIndex: 'description', 
        render: (t) => t || <Text type="secondary">—</Text> 
      },
      { 
        title: 'Category', 
        dataIndex: 'category', 
        render: (c) => <Tag color="blue">{c}</Tag> 
      },
      {
        title: 'Actions',
        width: 120,
        fixed: 'right',
        render: (_, record) => (
          <Space size="small">
            <Button
              icon={<FaEdit />}
              size="small"
              onClick={() => {
                setEditingId(record.permissionId);
                form.setFieldsValue(record);
                setModalVisible(true);
              }}
            />
            <Button 
              icon={<FaTrash />} 
              size="small" 
              danger 
              onClick={() => handleDeletePermission(record.permissionId)} 
            />
          </Space>
        ),
      },
    ],
    [form, handleDeletePermission],
  );

  // ─── helper components (memo) ──────────────────────────
  const RoleInfo = useMemo(() => {
    if (!selectedRole) {
      return (
        <Alert
          message="No Role Selected"
          description="Please select a role from the left panel to manage permissions."
          type="info"
          showIcon
        />
      );
    }
    
    const role = roles.find((x) => x.id === selectedRole);
    if (!role) return null;
    
    return (
      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions title="Selected Role" column={2} size="small">
          <Descriptions.Item label="ID">
            <Text code>{role.id}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Name">
            <Text strong>{role.name}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Permissions">
            <Badge count={rolePermissions.length} showZero color="blue" />
          </Descriptions.Item>
        </Descriptions>
      </Card>
    );
  }, [rolePermissions.length, roles, selectedRole]);

  const UserInfo = useMemo(() => {
    if (!selectedUser) {
      return (
        <Alert
          message="No User Selected"
          description="Please select a user from the left panel to manage roles."
          type="info"
          showIcon
        />
      );
    }
    
    const u = users.find((x) => x.id === selectedUser);
    if (!u) return null;
    
    return (
      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions title="Selected User" column={2} size="small">
          <Descriptions.Item label="ID">
            <Text code>{u.id}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Name">
            <Text strong>{u.firstName} {u.lastName}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Email">{u.email}</Descriptions.Item>
          <Descriptions.Item label="Roles">
            <Badge count={userRoles.length} showZero color="blue" />
          </Descriptions.Item>
        </Descriptions>
      </Card>
    );
  }, [selectedUser, userRoles.length, users]);

  const BulkButtons = useCallback(
    ({ type }) => {
      const ready = (type === 'role' && selectedRole) || (type === 'user' && selectedUser);
      if (!ready) return null;

      const ids = type === 'role' ? permissions.map((p) => p.permissionId) : roles.map((r) => r.id);
      const handler = type === 'role' ? bulkAssignPermissions : bulkAssignRoles;
      const title = type === 'role' ? 'permissions' : 'roles';

      return (
        <Space style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            onClick={() =>
              confirm({
                title: `Assign All ${title}?`,
                content: `This will assign all ${ids.length} ${title} to the selected ${type}.`,
                onOk: () => handler(ids),
              })
            }
          >
            Assign All
          </Button>
          <Button
            danger
            onClick={() =>
              confirm({
                title: `Remove All ${title}?`,
                content: `This will remove all ${title} from the selected ${type}.`,
                onOk: async () => {
                  setAssignLoading(true);
                  try {
                    if (type === 'role') {
                      await axiosInstance.delete(`/RolePermission/Roles/${selectedRole}/Permissions`);
                      setRolePermissions([]);
                    } else {
                      await axiosInstance.delete(`/User/${selectedUser}/Roles`);
                      setUserRoles([]);
                    }
                    notification.success({
                      message: 'Success',
                      description: `All ${title} removed successfully`,
                    });
                  } catch (err) {
                    notification.error({
                      message: 'Error',
                      description: `Failed to remove ${title}`,
                    });
                  } finally {
                    setAssignLoading(false);
                  }
                },
              })
            }
          >
            Remove All
          </Button>
        </Space>
      );
    },
    [bulkAssignPermissions, bulkAssignRoles, permissions, roles, selectedRole, selectedUser],
  );

  // Get current loading state based on active tab
  const getCurrentLoading = () => {
    switch (activeKey) {
      case '1': return permissionsLoading;
      case '2': return rolesLoading;
      case '3': return usersLoading;
      default: return false;
    }
  };

  // Get refresh function for current tab
  const getRefreshFunction = () => {
    switch (activeKey) {
      case '1': return fetchPermissions;
      case '2': return fetchRoles;
      case '3': return fetchUsers;
      default: return () => {};
    }
  };

  // ─── render ─────────────────────────────────────────────
  if (!authData?.hotelId) {
    return (
      <Alert
        message="Authentication Required"
        description="Please log in to access permission management."
        type="warning"
        showIcon
      />
    );
  }

  return (
    <div className="permission-manager">
      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              Permission Management
            </Title>
            <Button
              type="text"
              icon={<FaInfoCircle />}
              onClick={() =>
                Modal.info({
                  title: 'Permission Management Guide',
                  width: 500,
                  content: (
                    <div>
                      <p><strong>Three-Step Process:</strong></p>
                      <ol>
                        <li><strong>Define Permissions</strong> - Create and manage individual permissions</li>
                        <li><strong>Assign to Roles</strong> - Group permissions into roles</li>
                        <li><strong>Assign Roles to Users</strong> - Grant roles to users</li>
                      </ol>
                      <p><em>Tip: Use bulk actions for faster assignment.</em></p>
                    </div>
                  ),
                })
              }
            />
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<FaSyncAlt />}
              onClick={() => {
                const controller = new AbortController();
                getRefreshFunction()(controller.signal);
              }}
              loading={getCurrentLoading()}
              title="Refresh current tab data"
            >
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<FaPlus />}
              onClick={() => {
                setEditingId(null);
                form.resetFields();
                setModalVisible(true);
              }}
            >
              Add Permission
            </Button>
          </Space>
        }
      >
        <Tabs
          activeKey={activeKey}
          onChange={handleTabChange}
          destroyInactiveTabPane={false}
          animated={{ inkBar: true, tabPane: false }}
        >
          {/* 1️⃣ Permissions */}
          <TabPane
            key="1"
            tab={
              <span>
                <FaKey /> Permissions
                {permissionsLoading && <Spin size="small" style={{ marginLeft: 8 }} />}
              </span>
            }
          >
            <PermissionsTab
              permissions={permissions}
              permissionsLoading={permissionsLoading}
              error={error}
              permissionColumns={permissionColumns}
              form={form}
              setEditingId={setEditingId}
              setModalVisible={setModalVisible}
              handleDeletePermission={handleDeletePermission}
              setError={setError}
            />
          </TabPane>

          {/* 2️⃣ Role-Permissions */}
          <TabPane
            key="2"
            tab={
              <span>
                <FaUserShield /> Role-Permissions
                {rolesLoading && <Spin size="small" style={{ marginLeft: 8 }} />}
              </span>
            }
          >
            <RolePermissionsTab
              roles={roles}
              rolesLoading={rolesLoading}
              permissions={permissions}
              assignLoading={assignLoading}
              selectedRole={selectedRole}
              rolePermissions={rolePermissions}
              roleSearch={roleSearch}
              setRoleSearch={setRoleSearch}
              debouncedRoleSearch={debouncedRoleSearch}
              handleRoleSelect={handleRoleSelect}
              togglePermission={togglePermission}
              bulkAssignPermissions={bulkAssignPermissions}
              RoleInfo={RoleInfo}
              BulkButtons={BulkButtons}
            />
          </TabPane>

          {/* 3️⃣ User-Roles */}
          <TabPane
            key="3"
            tab={
              <span>
                <FaUsers /> User-Roles
                {usersLoading && <Spin size="small" style={{ marginLeft: 8 }} />}
              </span>
            }
          >
            <UserRolesTab
              users={users}
              usersLoading={usersLoading}
              roles={roles}
              assignLoading={assignLoading}
              selectedUser={selectedUser}
              userRoles={userRoles}
              userSearch={userSearch}
              setUserSearch={setUserSearch}
              debouncedUserSearch={debouncedUserSearch}
              handleUserSelect={handleUserSelect}
              toggleUserRole={toggleUserRole}
              bulkAssignRoles={bulkAssignRoles}
              UserInfo={UserInfo}
              BulkButtons={BulkButtons}
            />
          </TabPane>
        </Tabs>

        {/* modal for create / edit permission */}
        <Modal
          open={modalVisible}
          title={
            <Space>
              {editingId ? <FaEdit /> : <FaPlus />}
              {editingId ? 'Edit Permission' : 'Create Permission'}
            </Space>
          }
          onCancel={() => {
            form.resetFields();
            setModalVisible(false);
            setEditingId(null);
          }}
          onOk={editingId ? handleUpdatePermission : handleCreatePermission}
          confirmLoading={modalLoading}
          destroyOnClose
          okText={editingId ? 'Update' : 'Create'}
          cancelText="Cancel"
          width={500}
        >
          <Form layout="vertical" form={form} preserve={false}>
            <Form.Item
              label="Name"
              name="name"
              rules={[
                { required: true, message: 'Name is required' },
                { min: 2, message: 'Name must be at least 2 characters' }
              ]}
            >
              <Input placeholder="e.g., MaintenanceAccess" maxLength={50} />
            </Form.Item>
            <Form.Item 
              label="Description" 
              name="description"
              rules={[{ max: 200, message: 'Description cannot exceed 200 characters' }]}
            >
              <TextArea 
                rows={3} 
                placeholder="Brief description of what this permission allows..."
                maxLength={200}
                showCount
              />
            </Form.Item>
            <Form.Item
              label="Category"
              name="category"
              rules={[
                { required: true, message: 'Category is required' },
                { min: 2, message: 'Category must be at least 2 characters' }
              ]}
            >
              <Input 
                list="categories" 
                placeholder="e.g., Maintenance, Reports, Settings"
                maxLength={30}
              />
            </Form.Item>
            <datalist id="categories">
              {categories.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default PermissionManager;